import * as React from 'react';
import { getAnchorId, cn } from '@/helpers/utils';
import {
  IconChevronUp,
  IconChevronDown,
  IconChevronRight,
} from '@americanexpress/dls-icons';
import { useAnalyticsContext } from '@/context/analyticsContext';
import styles from './PageNavigation.module.scss';

interface MenuItem {
  type: 'chef' | 'partner' | 'faq' | 'terms';
  name?: string;
  title?: string;
  question?: string;
}

interface MenuSection {
  menuSectionHeader: string;
  items: MenuItem[];
  hideSectionHeader?: boolean;
}

interface PageNavigationProps {
  menus: MenuSection[];
  scrollContainerId?: string;
  showMobileView?: boolean;
}

// Main component
const PageNavigation: React.FC<PageNavigationProps> = ({
  menus,
  scrollContainerId,
  showMobileView = false,
}) => {
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [activeAnchor, setActiveAnchor] = React.useState<string | null>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const { customTrack, identifier } = useAnalyticsContext();
  const resolveScrollTarget = React.useCallback((): Window | HTMLElement => {
    if (!scrollContainerId) return window;
    return (
      document.querySelector<HTMLElement>(`#${scrollContainerId}`) || window
    );
  }, [scrollContainerId]);

  // Listen for scroll to update active nav link on desktop
  React.useEffect(() => {
    const scrollTarget = resolveScrollTarget();

    const handleScroll = () => {
      // Get all anchor ids in order
      const anchorIds: string[] = [];
      menus.forEach((section) => {
        section.items.forEach((item, idx) => {
          anchorIds.push(
            getAnchorId({
              type: item.type,
              name: item.name,
              title: item.title,
              idx,
              section: section.menuSectionHeader,
            })
          );
        });
      });
      // Find the anchor currently in view
      let found: string | null = null;
      anchorIds.forEach((anchorId) => {
        const el = document.querySelector(`#${anchorId}`);
        if (el) {
          const rect = el.getBoundingClientRect();
          const halfViewport =
            'scrollY' in scrollTarget
              ? window.innerHeight / 2
              : scrollTarget.getBoundingClientRect().top +
                scrollTarget.clientHeight / 2;
          const isAtOrAboveHalfScreen = rect.top <= halfViewport;
          if (isAtOrAboveHalfScreen) {
            found = anchorId;
          }
        }
      });
      setActiveAnchor(found);
    };

    scrollTarget.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    return () => scrollTarget.removeEventListener('scroll', handleScroll);
  }, [menus, resolveScrollTarget]);

  // Scroll to anchor on click, using window.scrollTo with offset to account for floating header
  const handleAnchorClick =
    (anchorId: string, linkText: string) =>
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault();

      customTrack({
        event: e,
        identifier,
        element: 'Table of Contents',
        detail: linkText,
      });

      const el = document.querySelector(`#${anchorId}`);
      if (el) {
        const scrollTarget = resolveScrollTarget();

        if ('scrollY' in scrollTarget) {
          const y = el.getBoundingClientRect().top + window.pageYOffset - 120;
          window.scrollTo({ top: y, behavior: 'smooth' });
        } else {
          const targetRect = scrollTarget.getBoundingClientRect();
          const y =
            el.getBoundingClientRect().top -
            targetRect.top +
            scrollTarget.scrollTop -
            120;
          scrollTarget.scrollTo({ top: y, behavior: 'smooth' });
        }
      }
      setActiveAnchor(anchorId);
    };

  // Render menu items (mobile-first, override with lg_ for desktop)
  const renderMenuItems = (
    items: MenuItem[],
    section: string,
    hideSectionHeader = false
  ) =>
    items.map((item: MenuItem, idx: number) => {
      const anchorId = getAnchorId({
        type: item.type,
        name: item.name,
        title: item.title,
        idx,
        section,
      });
      const linkText = item.title || item.name;
      if (!linkText) return null;
      // Add active class on desktop if this is the active anchor
      const isActive = activeAnchor === anchorId;
      const baseClass = cn(
        'tw-justify-between  tw-w-full   tw-flex tw-cursor-pointer tw-items-center tw-bg-none tw-px-0 tw-py-3 tw-text-left tw-font-sans tw-text-base tw-font-medium tw-leading-6 tw-text-blue-bright-shade-600-s8 ',
        {
          'lg_tw-max-w-48 lg_tw-border-solid lg_tw-border-b lg_tw-border-gray-300 hover:lg_tw-bg-gray-50 focus:lg_tw-outline-none  lg_tw-bg-none lg_tw-py-4 lg_tw-text-xs lg_tw-font-bold lg_tw-leading-5':
            !showMobileView,
        }
      );

      const activeClass = isActive
        ? 'lg_tw-text-blue-bright-shade-600-s8'
        : 'lg_tw-text-gray-700';
      return (
        <button
          key={anchorId}
          onClick={handleAnchorClick(anchorId, linkText)}
          aria-label={linkText}
          type="button"
          className={cn(baseClass, {
            [activeClass]: !showMobileView,
            'tw-pt-0 lg_tw-pt-0': idx === 0 && hideSectionHeader,
          })}
        >
          <span className="tw-font-inherit tw-block tw-break-words tw-pr-4">
            {linkText}
          </span>
          {/* Chevron only on desktop */}
          <IconChevronRight
            size="xs"
            isFilled={true}
            className={cn(
              `${styles.iconFillMinimal} tw-absolute tw-right-4 tw-hidden`,
              { 'lg_tw-inline': !showMobileView }
            )}
          />
        </button>
      );
    });
  // Mobile-first: render both, hide/show with Tailwind responsive classes
  return (
    <>
      {/* Mobile Navigation (below 1024px) */}
      <div
        className={cn(
          'tw-relative tw-col-span-8 tw-col-start-3 tw-mb-6 tw-block',
          { 'lg_tw-hidden': !showMobileView }
        )}
      >
        <div
          ref={menuRef}
          className="tw-relative tw-w-full tw-min-w-0 tw-rounded-2xl tw-bg-gray-100 tw-transition-all tw-duration-300 tw-ease-in-out"
        >
          {/* Menu items container with animated max-height */}
          <div
            className="tw-overflow-hidden tw-pt-6 tw-transition-all tw-duration-300 tw-ease-in-out"
            style={{ maxHeight: mobileOpen ? '2000px' : '328px' }}
          >
            {menus.map((section: MenuSection) => (
              <div key={section.menuSectionHeader} className="tw-px-6 tw-pb-6">
                {!section.hideSectionHeader && (
                  <div
                    className={cn(
                      'tw-mb-2 tw-font-sans tw-text-base tw-font-bold tw-leading-6 tw-text-gray-700',
                      { 'lg_tw-text-gray-900': !showMobileView }
                    )}
                  >
                    {section.menuSectionHeader}
                  </div>
                )}
                {renderMenuItems(
                  section.items,
                  section.menuSectionHeader,
                  section.hideSectionHeader
                )}
              </div>
            ))}
          </div>
          {/* Show More/Less button always below menu items, only visible on mobile */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            type="button"
            aria-label={mobileOpen ? 'Show Less' : 'Show More'}
            className="tw-mt-0 tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-b-2xl tw-border-t tw-border-solid tw-border-gray-300 tw-bg-gray-100 tw-pb-4dot5 tw-pl-6 tw-pr-2 tw-pt-4dot25 tw-text-base tw-leading-6 tw-text-gray-800"
          >
            <span>{mobileOpen ? 'Show Less' : 'Show More'}</span>
            <span className="tw-absolute tw-right-4 tw-top-1/2 -tw-translate-y-1/2">
              {mobileOpen ? (
                <IconChevronUp
                  size="xs"
                  isFilled={true}
                  color="tw-blue-bright-600"
                />
              ) : (
                <IconChevronDown
                  size="xs"
                  isFilled={true}
                  color="tw-blue-bright-600"
                />
              )}
            </span>
          </button>
        </div>
      </div>

      {/* Desktop Navigation (1024px and above) */}
      <nav
        className={cn('tw-hidden tw-w-full tw-pr-6', {
          'lg_tw-sticky lg_tw-block': !showMobileView,
        })}
        style={{ top: '90px' }}
        aria-label="Partner Navigation"
      >
        {menus.map((section: MenuSection, idx) => (
          <div key={section.menuSectionHeader}>
            {!section.hideSectionHeader && (
              <div
                className={cn(
                  'tw-mb-2 tw-font-sans tw-text-xs tw-font-bold tw-leading-6 tw-text-gray-700',
                  {
                    'lg_tw-text-gray-900': !showMobileView,
                    'tw-mt-6': idx !== 0,
                  }
                )}
              >
                {section.menuSectionHeader}
              </div>
            )}
            {renderMenuItems(
              section.items,
              section.menuSectionHeader,
              section.hideSectionHeader
            )}
          </div>
        ))}
      </nav>
    </>
  );
};

export default PageNavigation;
